import React, { useEffect, useRef, useState } from 'react';
import { factories, models, service } from 'powerbi-client';
import { useAuth } from '../../contexts/AuthContext';
import { powerBiService } from '../../services/api';
import { POWERBI_REPORT_ID, POWERBI_WORKSPACE_ID } from '../../config/powerbi';
import styles from './PowerBIEmbed.module.css';

const powerbi = new service.Service(
  factories.hpmFactory,
  factories.wpmpFactory,
  factories.routerFactory
);

const PowerBIEmbed = ({ reportId = POWERBI_REPORT_ID, pageName = '', fit = 'width' }) => {
  const containerRef = useRef(null);
  const { user } = useAuth();
  const isLoggedIn = !!user;
  const [status, setStatus] = useState(isLoggedIn ? 'loading' : 'signed-out');
  const [error, setError] = useState('');

  useEffect(() => {
    const container = containerRef.current;
    if (!isLoggedIn) {
      setStatus('signed-out');
      return undefined;
    }
    if (!reportId || !POWERBI_WORKSPACE_ID) {
      setStatus('error');
      setError('Power BI report is not configured.');
      return undefined;
    }

    let cancelled = false;
    setStatus('loading');
    setError('');

    const embedReport = async () => {
      const { token } = await powerBiService.getEmbedToken(reportId);
      if (cancelled || !container) return;

      const config = {
        type: 'report',
        tokenType: models.TokenType.Embed,
        accessToken: token,
        embedUrl: `https://app.powerbi.com/reportEmbed?reportId=${reportId}&groupId=${POWERBI_WORKSPACE_ID}&pageName=${pageName}`,
        id: reportId,
        permissions: models.Permissions.All,
        settings: {
          panes: {
            filters: { visible: false },
            pageNavigation: { visible: true }
          },
          layoutType: models.LayoutType.Custom,
          customLayout: {
            displayOption:
              fit === 'page' ? models.DisplayOption.FitToPage : models.DisplayOption.FitToWidth
          }
        }
      };

      try {
        powerbi.reset(container);
      } catch (_) {
        // The container has not been embedded yet.
      }

      const report = powerbi.embed(container, config);
      report.on('error', (event) => {
        if (cancelled) return;
        setStatus('error');
        setError(event?.detail?.message || 'The report could not be embedded.');
      });
      if (!cancelled) setStatus('ready');
    };

    embedReport().catch((err) => {
      if (cancelled) return;
      setStatus('error');
      setError(err.message || 'Failed to load the report.');
    });

    return () => {
      cancelled = true;
      if (container) {
        try {
          powerbi.reset(container);
        } catch (_) {
          // Ignore reset when embedding never started.
        }
      }
    };
  }, [isLoggedIn, reportId, pageName, fit]);

  return (
    <div className={styles.embedRoot}>
      <div ref={containerRef} className={styles.reportContainer} />
      {status !== 'ready' && (
        <div className={`${styles.overlay} ${status === 'error' ? styles.error : ''}`}>
          {status === 'signed-out' && 'Sign in to view this report.'}
          {status === 'loading' && 'Loading report...'}
          {status === 'error' && error}
        </div>
      )}
    </div>
  );
};

export default PowerBIEmbed;
